import type { ComponentType} from 'react';
import { useEffect, useState } from 'react';

import { getNowTimestamp } from '@shared/helpers/date';

import type { ProgressBarProps } from '@ui/uikit/components/common/ProgressBar';
import { ProgressBar } from '@ui/uikit/components/common/ProgressBar';
import type { TypographyColors } from '@ui/uikit/components/common/Typography';
import { Typography } from '@ui/uikit/components/common/Typography';
import { Time } from '@ui/uikit/components/icons/mono/Time';
import { TimeOngoing } from '@ui/uikit/components/icons/mono/TimeOngoing';
import { TimeUpcoming } from '@ui/uikit/components/icons/mono/TimeUpcoming';

import type { AdjacentRound } from '../../../../../../../model/AdjacentRound/adjacentRound';
import { PredictionRoundState } from '../../../../../../../model/AdjacentRound/roundState';

type StateConfigItem = {
  title: string;
  titleColor: TypographyColors;
  variant: ProgressBarProps['variant'];
  progress: ProgressBarProps['progress'];
  Icon: ComponentType<{ className: string }>;
  iconColor: TypographyColors;
};

const stateConfig: Record<PredictionRoundState, StateConfigItem> = {
  [PredictionRoundState.CLOSED]: {
    title: 'Previous',
    titleColor: 'tertiary',
    variant: 'secondary',
    progress: 'progress',
    Icon: Time,
    iconColor: 'tertiary',
  },
  [PredictionRoundState.LOCKED]: {
    title: 'Ongoing',
    titleColor: 'primary',
    variant: 'primary',
    progress: 'over',
    Icon: TimeOngoing,
    iconColor: 'pink',
  },
  [PredictionRoundState.ACTIVE]: {
    title: 'Upcoming',
    titleColor: 'tertiary',
    variant: 'secondary',
    progress: 'progress',
    Icon: TimeUpcoming,
    iconColor: 'tertiary',
  },
};

type PredictionRoundProgressBarProps = {
  round: AdjacentRound;
};

export const PredictionRoundProgressBar = ({ round }: PredictionRoundProgressBarProps) => {
  const [currentTimestamp, setCurrentTimestamp] = useState(getNowTimestamp);
  const { startTimestamp, closeTimestamp } = round;

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = getNowTimestamp();

      if (now >= closeTimestamp) {
        clearInterval(intervalId);
        setCurrentTimestamp(closeTimestamp);
      } else {
        setCurrentTimestamp(now);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [startTimestamp, closeTimestamp]);

  const isClosed = round.state === PredictionRoundState.CLOSED;
  const roundState = stateConfig[round.state];
  const Icon = roundState.Icon;
  return (
    <ProgressBar
      title={
        <div className="flex gap-2 items-center">
          <Typography variant="caption-s-strong" color={roundState.iconColor}>
            <Icon className="icon-xs" />
          </Typography>
          <Typography variant="caption-s-strong" color={roundState.titleColor}>
            {roundState.title}
          </Typography>
        </div>
      }
      subtitle={round.id}
      value={isClosed ? 0 : currentTimestamp}
      size="s"
      min={startTimestamp}
      max={closeTimestamp}
      variant={roundState.variant}
      progress={roundState.progress}
    />
  );
};
